import type { FindingData, FindingsMessageBlock, HeadingBlock, BulletsBlock, TextBlock, MessageBlock } from "@/lib/chat-types";
import type { JsonRecord } from "@/lib/fund-compliance/parse-primitives";
import { readString, resolveArray, resolveString, toRecord } from "@/lib/fund-compliance/parse-primitives";

/* ── Field resolution keys ───────────────────────────────────────── */

const ISSUE_KEYS = ["issue", "title", "summary"] as const;
const ISSUES_ARRAY_KEYS = ["issues", "findings"] as const;
const REGULATION_KEYS = ["regulation", "ruleId", "sourceTitle"] as const;
const RECOMMENDATION_KEYS = ["recommendation", "remediation", "action"] as const;
const ACTIONS_ARRAY_KEYS = ["recommendedActions", "actions", "recommendations"] as const;
const NOTES_KEYS = ["sourceBackedNotes", "notes"] as const;
const EVIDENCE_REFS_KEYS = ["evidenceRefs", "evidence", "citationRefs"] as const;

/* ── Severity mapping ────────────────────────────────────────────── */

function normalizeSeverity(raw: string | undefined): FindingData["severity"] {
  const lower = raw?.toLowerCase();
  if (lower === "critical" || lower === "high") return "critical";
  if (lower === "warning" || lower === "medium") return "warning";
  return "pass";
}

/* ── Structured JSON extraction ──────────────────────────────────── */

function parseFindings(issuesPayload: unknown[]): FindingData[] {
  return issuesPayload
    .map((item) => {
      const record = toRecord(item);
      if (!record) return null;

      const issue = resolveString(record, ISSUE_KEYS);
      if (!issue) return null;

      const evidenceRefs = (resolveArray(record, EVIDENCE_REFS_KEYS) ?? [])
        .map((value) => readString(value))
        .filter((value): value is string => Boolean(value));

      const finding: FindingData = {
        severity: normalizeSeverity(readString(record.severity)),
        issue,
        location: readString(record.location) ?? "Not specified",
        regulation: resolveString(record, REGULATION_KEYS) ?? "Not specified",
        recommendation: resolveString(record, RECOMMENDATION_KEYS) ?? "No recommendation provided",
      };

      if (evidenceRefs.length > 0) {
        finding.evidenceRefs = evidenceRefs;
      }

      return finding;
    })
    .filter((f): f is FindingData => f !== null);
}

const ACTION_TEXT_KEYS = ["text", "content", "title", "description", "action", "message", "label"] as const;

function parseActions(record: JsonRecord): string[] {
  const actionsPayload = resolveArray(record, ACTIONS_ARRAY_KEYS) ?? [];

  return actionsPayload
    .map((item) => {
      const actionRecord = toRecord(item);
      if (actionRecord) {
        return resolveString(actionRecord, ACTION_TEXT_KEYS);
      }
      return typeof item === "string" ? item : undefined;
    })
    .filter((s): s is string => s !== undefined);
}

/* ── Public API ──────────────────────────────────────────────────── */

/**
 * Extract review blocks from structured JSON review data.
 * Parses title, summary, findings, recommended actions, and notes.
 */
export function extractReviewBlocks(value: unknown): MessageBlock[] {
  const record = toRecord(value);
  if (!record) return [];

  const blocks: MessageBlock[] = [];
  const title = readString(record.title);
  const summary = readString(record.summary);

  if (title) {
    blocks.push({ type: "heading", content: title, level: 2 } satisfies HeadingBlock);
  }

  if (summary) {
    blocks.push({ type: "text", content: summary } satisfies TextBlock);
  }

  const issuesPayload = resolveArray(record, ISSUES_ARRAY_KEYS) ?? [];
  const findings = parseFindings(issuesPayload);

  if (findings.length > 0) {
    blocks.push({ type: "heading", content: "Compliance Findings", level: 3 } satisfies HeadingBlock);
    blocks.push({ type: "findings", findings } satisfies FindingsMessageBlock);
  }

  const actions = parseActions(record);
  if (actions.length > 0) {
    blocks.push({ type: "heading", content: "Recommended Actions", level: 3 } satisfies HeadingBlock);
    blocks.push({ type: "bullets", items: actions } satisfies BulletsBlock);
  }

  const sourceBackedNotes = resolveString(record, NOTES_KEYS);
  if (sourceBackedNotes) {
    blocks.push({ type: "heading", content: "Source-backed Notes", level: 3 } satisfies HeadingBlock);
    blocks.push({ type: "text", content: sourceBackedNotes } satisfies TextBlock);
  }

  return blocks;
}

/**
 * FALLBACK PARSER: Extract structured findings from raw markdown text
 * when the backend sends findings as formatted text rather than JSON.
 *
 * Parses the pattern: [critical] Issue text - Location: ... - Regulation: ... - Recommendation: ...
 *
 * This function should be considered deprecated in favor of structured
 * JSON payloads from metadata.review.issues[].
 */
export function extractStructuredFindingsFromText(text: string): FindingData[] {
  if (!text) return [];

  // Field labels may be wrapped in markdown bold, e.g. `**Location**:`, or plain `Location:`.
  // The `\*{0,2}` handles zero or two asterisks around the label.
  const SPLIT_RE = /\s+-\s+\*{0,2}(?:Location|Regulation|Recommendation)\*{0,2}:/i;
  const LOCATION_RE = /-\s+\*{0,2}Location\*{0,2}:\s*([\s\S]*?)(?=\s+-\s+\*{0,2}(?:Regulation|Recommendation)\*{0,2}:|$)/i;
  const REGULATION_RE = /-\s+\*{0,2}Regulation\*{0,2}:\s*([\s\S]*?)(?=\s+-\s+\*{0,2}Recommendation\*{0,2}:|$)/i;
  const RECOMMENDATION_RE = /-\s+\*{0,2}Recommendation\*{0,2}:\s*([\s\S]*?)$/i;

  return Array.from(
    text.matchAll(
      /\[(critical|warning|pass)\]\s*([\s\S]*?)(?=\[(critical|warning|pass)\]|##\s|$)/gi
    )
  )
    .map((match) => {
      const severity = match[1]?.toLowerCase() as FindingData["severity"] | undefined;
      const body = match[2]?.trim();
      if (!severity || !body) return null;

      const issue = body.split(SPLIT_RE)[0]?.trim();
      if (!issue) return null;

      const location = body.match(LOCATION_RE)?.[1]?.trim() ?? "Not specified";
      const regulation = body.match(REGULATION_RE)?.[1]?.trim() ?? "Not specified";
      const recommendation = body.match(RECOMMENDATION_RE)?.[1]?.trim() ?? "No recommendation provided";

      return { severity, issue, location, regulation, recommendation } satisfies FindingData;
    })
    .filter((f): f is FindingData => f !== null);
}
